import { COLOR_OPTIONS } from 'config/style';
import STTypo, { STTypoModels } from 'core/shared/typo';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import type { FeatureCollection, Point } from 'geojson';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import variablePie from 'highcharts/modules/variable-pie';
import _ from 'lodash';
import { useFiltersStore } from '@features/regional-overview/components/filters/filters.store';
import MockedDataAlertRegionalContainer from '@features/regional-overview/components/mocked-data-alert/mocked-data-alert.container';
import { ALL_FILTER_VALUE, RegionalOverviewFilterType } from '@features/regional-overview/regional-overview.models';
import type { RegionalOverviewProperties } from 'querys/regional-overview/regional-overview.query.models';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { AppState } from 'redux/app-state';
import type { IHighLevelStatistics } from '../regional-overview-mycotoxin.models';
import { formatArea, getTypeArea, handleDataStatisticsMycotoxin, optionsStatistics } from '../regional-overview-mycotoxin.utils';
import RegionalMycotoxinItemListStatistics from './components/regional-mycotoxin-item-list-statistics';
import './regional-mycotoxin-statistics-container.less';

variablePie(Highcharts);

interface IRegionalMycotoxinStatisticsContainer {
  dataFiltered?: FeatureCollection<Point, RegionalOverviewProperties>;
}
const RegionalMycotoxinStatisticsContainer = ({ dataFiltered }: IRegionalMycotoxinStatisticsContainer): JSX.Element => {
  const [t] = useTranslation();

  const currentUnitSystem = useSelector((state: AppState) => state.uiState.global.currentUnitSystem);
  const applyValue = useFiltersStore(state => state.applyValue);
  const { track } = useSegmentTracking();
  const [dataStatisticsMycotoxin, setDataStatisticsMycotoxin] = useState<IHighLevelStatistics[]>([]);
  const [dataChartsStatisticMycotoxin, setDataChartsStatisticMycotoxin] = useState<IHighLevelStatistics>();
  const [totalHac, setTotalHac] = useState<string>();

  useEffect(() => {
    if (dataFiltered) {
      const dataFormatted = handleDataStatisticsMycotoxin(dataFiltered, currentUnitSystem);
      setDataStatisticsMycotoxin(dataFormatted);
    }
  }, [dataFiltered, currentUnitSystem]);

  useEffect(() => {
    if (dataStatisticsMycotoxin) {
      const dataOptions = optionsStatistics(currentUnitSystem);
      setDataChartsStatisticMycotoxin({
        ...dataOptions,
        series: [
          {
            ...(dataOptions?.series?.[0] ?? {}),
            data: dataStatisticsMycotoxin
          }
        ],
        plotOptions: {
          series: {
            allowPointSelect: true,
            point: {
              events: {
                click: (event: { point: { selected: boolean; riskIndex: string } }) => {
                  const appliedValue = event.point.selected ? ALL_FILTER_VALUE : event.point.riskIndex;
                  applyValue(RegionalOverviewFilterType.MYCOTOXIN_RISK, appliedValue);
                  track('Mycotoxin Risk - Filtered by Agro-Climatic Risk', { riskIndex: appliedValue });
                }
              }
            }
          }
        }
      });
      setTotalHac(
        formatArea(
          _.sumBy(dataStatisticsMycotoxin, item => Number.parseFloat(item?.areaSize)),
          currentUnitSystem
        )
      );
    }
  }, [dataStatisticsMycotoxin, currentUnitSystem, applyValue, track]);

  return (
    <div className='high-level-statistics-container' data-testid='regional-risk-mycotoxin-statistics-container'>
      <MockedDataAlertRegionalContainer style={{ marginBottom: '16px' }} />
      <STTypo
        data-testid='regional-risk-mycotoxin-statistics-container-title'
        color={COLOR_OPTIONS.NEUTRAL_80}
        type={STTypoModels.TypeOptions.P1}
        boldness>
        {t('pages.regional_overview.regional_mycotoxin.high_level_statistics.agro_climatic_risk')}
      </STTypo>

      <div className='high-level-statistics-container__chart'>
        <div className='total'>
          <STTypo color={COLOR_OPTIONS.NEUTRAL_60} type={STTypoModels.TypeOptions.P1}>
            {t('pages.regional_overview.regional_mycotoxin.high_level_statistics.total')}
          </STTypo>
          <STTypo
            color={COLOR_OPTIONS.NEUTRAL_80}
            type={STTypoModels.TypeOptions.H5}
            boldness
            data-testid='regional-risk-mycotoxin-statistics-total-area'>
            {totalHac} {getTypeArea(currentUnitSystem)}
          </STTypo>
        </div>
        {dataChartsStatisticMycotoxin && (
          <HighchartsReact
            data-testid='regional-risk-mycotoxin-statistics-chart'
            highcharts={Highcharts}
            containerProps={{ className: 'chartContainer' }}
            options={dataChartsStatisticMycotoxin}
          />
        )}
      </div>

      <div className='high-level-statistics-container__list-climatic-risk' data-testid='regional-risk-mycotoxin-statistics-list-items'>
        <RegionalMycotoxinItemListStatistics dataStatisticsMycotoxin={dataStatisticsMycotoxin} />
      </div>
    </div>
  );
};

export default RegionalMycotoxinStatisticsContainer;
